import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * 表单详情导出工具类
 * 提供表单详情页面导出为图片和PDF的功能
 * 可在任何页面中使用，只需传入容器元素和Vue实例
 */

/**
 * 准备导出环境：展开表格、移除滚动限制、记录原始样式
 * @param {HTMLElement} container - 容器元素
 * @param {Object} vueInstance - Vue实例，用于访问tableCollapsed和$nextTick
 * @returns {Object} 返回原始样式和表格状态，用于后续恢复
 */
async function prepareExportEnvironment(container, vueInstance) {
  // 找到所有滚动容器和表单元素
  const scrollContainer = container.querySelector('.commonscroll') || container.querySelector('.el-scrollbar')
  const scrollWrap = container.querySelector('.el-scrollbar__wrap') || container.querySelector('.el-scrollbar__view')
  const formElement = container.querySelector('.el-form') || container.querySelector('form')
  
  // 找到所有表格的滚动容器
  const tableBodyWrappers = container.querySelectorAll('.el-table__body-wrapper')
  const tableHeaderWrappers = container.querySelectorAll('.el-table__header-wrapper')
  const tableInnerWrappers = container.querySelectorAll('.el-table__inner-wrapper')
  const allTables = container.querySelectorAll('.el-table')
  
  // 展开所有折叠的表格（通过 Vue 响应式数据控制）
  const originalTableCollapsed = JSON.parse(JSON.stringify(vueInstance.tableCollapsed || {}))
  if (vueInstance.tableCollapsed) {
    Object.keys(vueInstance.tableCollapsed).forEach(key => {
      if (vueInstance.tableCollapsed[key] === true) {
        vueInstance.$set(vueInstance.tableCollapsed, key, false)
      }
    })
  }
  
  // 等待表格展开
  await vueInstance.$nextTick()
  
  // 记录原始样式
  const originalStyles = {
    container: {
      height: container.style.height,
      overflow: container.style.overflow,
      maxHeight: container.style.maxHeight,
      position: container.style.position
    },
    scrollContainer: scrollContainer ? {
      height: scrollContainer.style.height,
      overflow: scrollContainer.style.overflow,
      maxHeight: scrollContainer.style.maxHeight
    } : null,
    scrollWrap: scrollWrap ? {
      height: scrollWrap.style.height,
      overflow: scrollWrap.style.overflow,
      maxHeight: scrollWrap.style.maxHeight,
      overflowY: scrollWrap.style.overflowY,
      overflowX: scrollWrap.style.overflowX
    } : null,
    tableBodyWrappers: [],
    tableHeaderWrappers: [],
    tableInnerWrappers: [],
    tables: []
  }

  // 记录表格滚动容器的原始样式
  tableBodyWrappers.forEach(wrapper => {
    originalStyles.tableBodyWrappers.push({
      element: wrapper,
      height: wrapper.style.height,
      overflow: wrapper.style.overflow,
      overflowX: wrapper.style.overflowX,
      overflowY: wrapper.style.overflowY,
      maxHeight: wrapper.style.maxHeight,
      maxWidth: wrapper.style.maxWidth
    })
  })
  
  tableHeaderWrappers.forEach(wrapper => {
    originalStyles.tableHeaderWrappers.push({
      element: wrapper,
      overflow: wrapper.style.overflow,
      overflowX: wrapper.style.overflowX,
      overflowY: wrapper.style.overflowY
    })
  })
  
  tableInnerWrappers.forEach(wrapper => {
    originalStyles.tableInnerWrappers.push({
      element: wrapper,
      overflow: wrapper.style.overflow,
      overflowX: wrapper.style.overflowX,
      overflowY: wrapper.style.overflowY
    })
  })
  
  allTables.forEach(table => {
    originalStyles.tables.push({
      element: table,
      width: table.style.width,
      maxWidth: table.style.maxWidth
    })
  })

  // 临时移除高度限制，让内容完全展开
  container.style.height = 'auto'
  container.style.overflow = 'visible'
  container.style.maxHeight = 'none'
  container.style.position = 'relative'
  
  if (scrollContainer) {
    scrollContainer.style.height = 'auto'
    scrollContainer.style.overflow = 'visible'
    scrollContainer.style.maxHeight = 'none'
  }
  
  if (scrollWrap) {
    scrollWrap.style.height = 'auto'
    scrollWrap.style.overflow = 'visible'
    scrollWrap.style.maxHeight = 'none'
    scrollWrap.style.overflowY = 'visible'
    scrollWrap.style.overflowX = 'visible'
  }
  
  // 移除表格滚动容器的滚动限制
  tableBodyWrappers.forEach(wrapper => {
    wrapper.style.height = 'auto'
    wrapper.style.overflow = 'visible'
    wrapper.style.overflowX = 'visible'
    wrapper.style.overflowY = 'visible'
    wrapper.style.maxHeight = 'none'
    wrapper.style.maxWidth = 'none'
  })
  
  tableHeaderWrappers.forEach(wrapper => {
    wrapper.style.overflow = 'visible'
    wrapper.style.overflowX = 'visible'
    wrapper.style.overflowY = 'visible'
  })
  
  tableInnerWrappers.forEach(wrapper => {
    wrapper.style.overflow = 'visible'
    wrapper.style.overflowX = 'visible'
    wrapper.style.overflowY = 'visible'
  })
  
  // 让表格宽度自适应内容
  allTables.forEach(table => {
    table.style.width = 'auto'
    table.style.maxWidth = 'none'
  })

  // 等待DOM更新和图片加载
  await vueInstance.$nextTick()
  await new Promise(resolve => setTimeout(resolve, 500)) // 等待内容展开和图片加载

  return {
    originalStyles,
    originalTableCollapsed,
    scrollContainer,
    scrollWrap,
    formElement,
    container
  }
}

/**
 * 恢复导出环境：恢复原始样式和表格状态
 * @param {Object} envData - prepareExportEnvironment返回的数据
 * @param {Object} vueInstance - Vue实例
 */
function restoreExportEnvironment(envData, vueInstance) {
  const { originalStyles, originalTableCollapsed, scrollContainer, scrollWrap, container } = envData
  
  if (!originalStyles || !container) {
    return
  }

  // 恢复容器样式
  if (originalStyles.container) {
    container.style.height = originalStyles.container.height
    container.style.overflow = originalStyles.container.overflow
    container.style.maxHeight = originalStyles.container.maxHeight
    container.style.position = originalStyles.container.position
  }
  
  if (scrollContainer && originalStyles.scrollContainer) {
    scrollContainer.style.height = originalStyles.scrollContainer.height
    scrollContainer.style.overflow = originalStyles.scrollContainer.overflow
    scrollContainer.style.maxHeight = originalStyles.scrollContainer.maxHeight
  }
  
  if (scrollWrap && originalStyles.scrollWrap) {
    scrollWrap.style.height = originalStyles.scrollWrap.height
    scrollWrap.style.overflow = originalStyles.scrollWrap.overflow
    scrollWrap.style.maxHeight = originalStyles.scrollWrap.maxHeight
    scrollWrap.style.overflowY = originalStyles.scrollWrap.overflowY
    scrollWrap.style.overflowX = originalStyles.scrollWrap.overflowX
  }
  
  // 恢复表格滚动容器的样式
  if (originalStyles.tableBodyWrappers) {
    originalStyles.tableBodyWrappers.forEach(state => {
      if (state && state.element) {
        state.element.style.height = state.height
        state.element.style.overflow = state.overflow
        state.element.style.overflowX = state.overflowX
        state.element.style.overflowY = state.overflowY
        state.element.style.maxHeight = state.maxHeight
        state.element.style.maxWidth = state.maxWidth
      }
    })
  }
  
  if (originalStyles.tableHeaderWrappers) {
    originalStyles.tableHeaderWrappers.forEach(state => {
      if (state && state.element) {
        state.element.style.overflow = state.overflow
        state.element.style.overflowX = state.overflowX
        state.element.style.overflowY = state.overflowY
      }
    })
  }
  
  if (originalStyles.tableInnerWrappers) {
    originalStyles.tableInnerWrappers.forEach(state => {
      if (state && state.element) {
        state.element.style.overflow = state.overflow
        state.element.style.overflowX = state.overflowX
        state.element.style.overflowY = state.overflowY
      }
    })
  }
  
  if (originalStyles.tables) {
    originalStyles.tables.forEach(state => {
      if (state && state.element) {
        state.element.style.width = state.width
        state.element.style.maxWidth = state.maxWidth
      }
    })
  }
  
  // 恢复表格折叠状态
  if (originalTableCollapsed && vueInstance.tableCollapsed) {
    Object.keys(originalTableCollapsed).forEach(key => {
      vueInstance.$set(vueInstance.tableCollapsed, key, originalTableCollapsed[key])
    })
  }
}

/**
 * 计算内容尺寸（包括表格内容）
 * @param {HTMLElement} container - 容器元素
 * @param {HTMLElement} scrollContainer - 滚动容器
 * @param {HTMLElement} scrollWrap - 滚动包装器
 * @param {HTMLElement} formElement - 表单元素
 * @returns {Object} 返回内容的高度和宽度
 */
function calculateContentSize(container, scrollContainer, scrollWrap, formElement) {
  let contentHeight = 0
  let contentWidth = 0
  
  if (formElement) {
    contentHeight = Math.max(contentHeight, formElement.scrollHeight)
    contentWidth = Math.max(contentWidth, formElement.scrollWidth)
  }
  if (scrollWrap) {
    contentHeight = Math.max(contentHeight, scrollWrap.scrollHeight)
    contentWidth = Math.max(contentWidth, scrollWrap.scrollWidth)
  }
  if (scrollContainer) {
    contentHeight = Math.max(contentHeight, scrollContainer.scrollHeight)
    contentWidth = Math.max(contentWidth, scrollContainer.scrollWidth)
  }
  
  // 检查所有表格的实际尺寸
  const allTables = container.querySelectorAll('.el-table')
  const tableBodyWrappers = container.querySelectorAll('.el-table__body-wrapper')
  
  allTables.forEach(table => {
    const tableWidth = table.scrollWidth || table.offsetWidth
    const tableHeight = table.scrollHeight || table.offsetHeight
    contentWidth = Math.max(contentWidth, tableWidth)
    contentHeight = Math.max(contentHeight, tableHeight)
  })
  
  tableBodyWrappers.forEach(wrapper => {
    contentWidth = Math.max(contentWidth, wrapper.scrollWidth || wrapper.offsetWidth)
    contentHeight = Math.max(contentHeight, wrapper.scrollHeight || wrapper.offsetHeight)
  })
  
  contentHeight = Math.max(contentHeight, container.scrollHeight)
  contentWidth = Math.max(contentWidth, container.scrollWidth)

  return { contentHeight, contentWidth }
}

/**
 * 导出表单详情为图片
 * @param {HTMLElement} container - 要导出的容器元素
 * @param {Object} vueInstance - Vue实例
 * @param {Object} options - 导出选项
 * @param {string} options.fileName - 文件名（不含扩展名）
 * @param {string} options.formId - 表单ID，用于生成文件名
 * @returns {Promise<Object>} 返回导出结果 { success: boolean, fileName: string, error?: string }
 */
export async function exportFormDetailAsImage(container, vueInstance, options = {}) {
  const { fileName, formId } = options
  let envData = null
  
  try {
    if (!container) {
      throw new Error('未找到要导出的内容')
    }

    // 准备导出环境
    envData = await prepareExportEnvironment(container, vueInstance)
    const { scrollContainer, scrollWrap, formElement, originalStyles } = envData

    // 计算内容尺寸
    const { contentHeight, contentWidth } = calculateContentSize(
      container,
      scrollContainer,
      scrollWrap,
      formElement
    )

    // 使用html2canvas生成图片
    const targetElement = formElement || scrollWrap || scrollContainer || container
    const canvas = await html2canvas(targetElement, {
      backgroundColor: '#ffffff',
      scale: 2, // 提高清晰度
      useCORS: true, // 允许跨域图片
      logging: false,
      width: contentWidth,
      height: contentHeight,
      windowWidth: contentWidth,
      windowHeight: contentHeight,
      scrollX: 0,
      scrollY: 0,
      allowTaint: true, // 允许跨域图片
      removeContainer: false // 保留容器
    })

    // 恢复原始样式
    restoreExportEnvironment(envData, vueInstance)

    // 将canvas转换为图片并下载
    const imgData = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    const finalFileName = fileName || `详情_${formId || 'detail'}_${new Date().getTime()}.png`
    link.download = finalFileName
    link.href = imgData
    link.click()

    return {
      success: true,
      fileName: finalFileName
    }
  } catch (error) {
    console.error('导出图片失败:', error)
    
    // 确保即使出错也恢复样式
    if (envData) {
      try {
        restoreExportEnvironment(envData, vueInstance)
      } catch (restoreError) {
        console.error('恢复样式失败:', restoreError)
      }
    }
    
    return {
      success: false,
      error: error.message || '导出图片失败，请稍后重试'
    }
  }
}

/**
 * 导出表单详情为PDF
 * @param {HTMLElement} container - 要导出的容器元素
 * @param {Object} vueInstance - Vue实例
 * @param {Object} options - 导出选项
 * @param {string} options.fileName - 文件名（不含扩展名）
 * @param {string} options.formId - 表单ID，用于生成文件名
 * @returns {Promise<Object>} 返回导出结果 { success: boolean, fileName: string, error?: string }
 */
export async function exportFormDetailAsPDF(container, vueInstance, options = {}) {
  const { fileName, formId } = options
  let envData = null
  
  try {
    if (!container) {
      throw new Error('未找到要导出的内容')
    }

    // 准备导出环境
    envData = await prepareExportEnvironment(container, vueInstance)
    const { scrollContainer, scrollWrap, formElement, originalStyles } = envData

    // 计算内容尺寸
    const { contentHeight, contentWidth } = calculateContentSize(
      container,
      scrollContainer,
      scrollWrap,
      formElement
    )

    // 使用html2canvas生成图片
    const targetElement = formElement || scrollWrap || scrollContainer || container
    const canvas = await html2canvas(targetElement, {
      backgroundColor: '#ffffff',
      scale: 2, // 提高清晰度
      useCORS: true, // 允许跨域图片
      logging: false,
      width: contentWidth,
      height: contentHeight,
      windowWidth: contentWidth,
      windowHeight: contentHeight,
      scrollX: 0,
      scrollY: 0,
      allowTaint: true, // 允许跨域图片
      removeContainer: false // 保留容器
    })

    // 恢复原始样式
    restoreExportEnvironment(envData, vueInstance)

    // 将canvas转换为PDF
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({
      orientation: contentWidth > contentHeight ? 'landscape' : 'portrait',
      unit: 'px',
      format: [contentWidth, contentHeight]
    })

    // 添加图片到PDF
    pdf.addImage(imgData, 'PNG', 0, 0, contentWidth, contentHeight)

    // 下载PDF
    const finalFileName = fileName || `详情_${formId || 'detail'}_${new Date().getTime()}.pdf`
    pdf.save(finalFileName)

    return {
      success: true,
      fileName: finalFileName
    }
  } catch (error) {
    console.error('导出PDF失败:', error)
    
    // 确保即使出错也恢复样式
    if (envData) {
      try {
        restoreExportEnvironment(envData, vueInstance)
      } catch (restoreError) {
        console.error('恢复样式失败:', restoreError)
      }
    }
    
    return {
      success: false,
      error: error.message || '导出PDF失败，请稍后重试'
    }
  }
}

